import { DB_EVENT_KINDS, DbRecentResult } from '@apm/contracts';
import { useMemo, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { DB_EVENT_LABEL, TIP } from '../terms';
import { formatCount, formatMs } from '../time';
import {
  Button,
  EmptyState,
  ErrorText,
  Field,
  LimitNote,
  Page,
  type Range,
  RangePicker,
  rangeMs,
  Section,
  Select,
  Stat,
} from '../ui';
import { DbEventTable } from './DbEventTable';
import { useDbEvents } from './useDbEvents';

/**
 * 화면 — DB 감시 (9회차 결정 4).
 *
 * 잠금 기다림·막힘·열어 둔 채 쉬는 트랜잭션·오래 도는 쿼리를 기간과 종류로 본다.
 * 행의 요청을 누르면 그 요청의 상세로 간다.
 *
 * 19회차에 기간을 시각으로 직접 넣을 수 있게 됐고(후보 #17), 감시기 자체의 상태가 위에 붙었다
 * (콘솔 검토 C-13). 서버는 `GET /api/db/recent`로 그 값을 처음부터 주고 있었는데
 * 콘솔이 부르지 않아 "감시가 붙었는지"를 스냅샷 유무로 짐작해야 했다.
 */

const PAGE_SIZE = 200;

export function DbEventsPage({
  range,
  onRangeChange,
}: {
  /** 진단 하위 탭이 함께 쓰는 기간 (22-B회차) */
  range: Range;
  onRangeChange: (next: Range) => void;
}) {
  const [kind, setKind] = useState('');
  const bounds = useMemo(() => rangeMs(range), [range]);

  const { loading, rows, total, truncated, error, reload } = useDbEvents({
    from: bounds.from,
    to: bounds.to,
    kind: kind === '' ? undefined : kind,
    limit: PAGE_SIZE,
  });

  // 감시기 자체가 도는지. 스냅샷은 하나만 받으면 충분하다
  const watcher = useQuery('/api/db/recent?minutes=1', DbRecentResult);
  const stats = watcher.data?.stats;

  return (
    <Page>
      <Section
        boxed
        title="DB 감시 상태"
        note="대상 DB를 1초마다 조회하는 감시가 정상 동작 중인지"
        actions={
          <Button size="sm" onClick={watcher.reload} disabled={watcher.loading}>
            {watcher.loading ? '읽는 중…' : '새로고침'}
          </Button>
        }
      >
        {watcher.error !== undefined ? <ErrorText>{watcher.error}</ErrorText> : null}
        {stats === undefined ? null : stats.enabled === false ? (
          <EmptyState
            title="DB 감시가 꺼져 있다"
            hint="관제 서버에 APM_DB_WATCH_URL을 설정하면 잠금·막힘을 기록한다"
          />
        ) : (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat
              label="연결"
              value={stats.connected ? '연결됨' : '끊김'}
              note={stats.database}
              level={stats.connected ? 'normal' : 'danger'}
              warnLabel="확인 필요"
            />
            <Stat label="조회 횟수" value={`${formatCount(stats.ticks)}회`} />
            <Stat
              label="실패"
              value={`${formatCount(stats.errors)}회`}
              note={stats.reconnects > 0 ? `재연결 ${stats.reconnects}회` : undefined}
              level={stats.errors > 0 ? 'warn' : 'normal'}
              warnLabel="있음"
            />
            <Stat
              label="기록한 사건"
              value={`${formatCount(stats.events)}건`}
              note={stats.openEvents > 0 ? `진행 중 ${stats.openEvents}건` : undefined}
            />
            <Stat
              label="조회 시간(평균)"
              value={formatMs(stats.avgQueryMs)}
              note={`마지막 ${formatMs(stats.lastQueryMs)}`}
            />
            <Stat
              label="요청 매칭률"
              value={`${Math.round(stats.matchRate * 100)}%`}
              level={stats.matchRate < 0.9 ? 'warn' : 'normal'}
              warnLabel="낮음"
              note={`SQL은 앞 ${formatCount(stats.trackActivityQuerySize)}자까지만 본다`}
            />
          </dl>
        )}
      </Section>

      <Section
        boxed
        title="DB 사건"
        note={[
          'DB 감시가 기록한 잠금 대기·막힘·유휴 트랜잭션·긴 쿼리.',
          <>
            <span title={TIP.dbTraceComment}>요청</span>을 누르면 상세로 간다
          </>,
        ]}
        badge={
          rows.length > 0 ? `${formatCount(total)}건 중 ${formatCount(rows.length)}건` : undefined
        }
        actions={
          <Button size="sm" onClick={reload} disabled={loading}>
            {loading ? '읽는 중…' : '새로고침'}
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-end gap-4">
          <RangePicker idPrefix="dbevents" value={range} onChange={onRangeChange} />
          <div className="w-48">
            <Field label="종류" htmlFor="dbevents-kind">
              <Select
                id="dbevents-kind"
                value={kind}
                onChange={setKind}
                options={[
                  { value: '', label: '전체' },
                  ...DB_EVENT_KINDS.map((item) => ({
                    value: item,
                    label: DB_EVENT_LABEL[item],
                  })),
                ]}
              />
            </Field>
          </div>
        </div>

        {error !== undefined ? <ErrorText>{error}</ErrorText> : null}
        {truncated ? (
          <p className="mb-3 whitespace-pre-line text-xs text-fail">{TIP.truncated}</p>
        ) : null}

        {!loading && error === undefined && rows.length === 0 ? (
          <EmptyState title="그 기간에 사건이 없다" hint="잠금 대기도 긴 트랜잭션도 없었다" />
        ) : null}

        {rows.length > 0 ? <DbEventTable rows={rows} /> : null}
        <LimitNote shown={rows.length} total={total} hint="기간을 좁히거나 종류를 골라 볼 것" />
      </Section>
    </Page>
  );
}
